import React, { useState, useRef, useEffect } from 'react';
import { splitSupervision, sortKosherCerts, certOptionLabel } from './api';
import { t } from './i18n';

// A search-with-suggestions picker for a place's kosher supervision. Replaces
// the old two-input <datalist> combo. Shows current certifiers as removable
// chips, filters the certifier list as you type, and lets you add a free-text
// name for a one-off hechsher (stored as plain text, no logo/DB record). The
// value is the comma-joined supervision string the rest of the app expects.
const C = { primary: '#1a4d8f', border: '#e2e8f0', muted: '#6b7280' };

export default function SupervisionPicker({ value, onChange, certs = [], placeholder = t('Search certifiers, or type a one-off name') }) {
  const opts = sortKosherCerts(certs || []);
  const names = new Set(opts.map((c) => c.name.toLowerCase()));
  const tokens = splitSupervision(value || '', names);

  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  // "Armed" once the field is already empty, so a backspace that merely finishes
  // deleting a typed word doesn't immediately remove the previous chip. A second,
  // deliberate backspace on the (already-empty) field removes the last chip.
  const armedRef = useRef(false);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const setTokens = (arr) => onChange(arr.filter(Boolean).join(', '));
  const addToken = (name) => {
    const t = (name || '').trim();
    if (t && !tokens.some((x) => x.toLowerCase() === t.toLowerCase())) setTokens([...tokens, t]);
    setInput(''); setOpen(false); setHi(0);
  };
  const removeToken = (i) => setTokens(tokens.filter((_, idx) => idx !== i));

  const q = input.trim().toLowerCase();
  const chosen = new Set(tokens.map((t) => t.toLowerCase()));
  // Match on name OR city so a certifier whose name doesn't contain its place
  // (e.g. "Chabad Maharal Center" in Prague) still surfaces when you type "prague".
  const matches = (q ? opts.filter((c) => c.name.toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q)) : opts)
    .filter((c) => !chosen.has(c.name.toLowerCase())).slice(0, 8);
  // Offer a free-text add when what's typed isn't already an exact cert or chip.
  const canAddFree = !!q && !opts.some((c) => c.name.toLowerCase() === q) && !chosen.has(q);
  const rows = matches.length + (canAddFree ? 1 : 0);

  const onKey = (e) => {
    // Any key other than a backspace-on-empty-field disarms the chip-delete.
    if (e.key !== 'Backspace' || input) armedRef.current = false;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && hi < matches.length && matches[hi]) addToken(matches[hi].name);
      else if (input.trim()) addToken(input);
    } else if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, rows - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Backspace' && !input && tokens.length) {
      // First backspace on the empty field arms; a second, deliberate one removes
      // the last chip — so deleting a word never eats the previous certifier.
      if (armedRef.current) { removeToken(tokens.length - 1); armedRef.current = false; }
      else armedRef.current = true;
    }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      {tokens.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {tokens.map((tok, i) => {
            const known = names.has(tok.toLowerCase());
            return (
              <span key={`${tok}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: known ? '#e0edff' : '#fff7ed', color: known ? C.primary : '#9a3412', border: `1px solid ${known ? '#bfdbfe' : '#fed7aa'}`, borderRadius: 999, padding: '4px 4px 4px 10px', fontSize: 13, fontWeight: 600 }}>
                <span>{known ? '✡️' : '✍️'} {tok}</span>
                <button type="button" onClick={() => removeToken(i)} aria-label={`${t('Remove')} ${tok}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
              </span>
            );
          })}
        </div>
      )}
      <input
        value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); setHi(0); armedRef.current = false; }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={tokens.length ? t('Add another…') : placeholder}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14 }}
      />
      {open && rows > 0 && (
        <div style={{ position: 'absolute', zIndex: 40, left: 0, right: 0, background: 'white', border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', marginTop: 4, maxHeight: 280, overflowY: 'auto' }}>
          {matches.map((c, i) => (
            <div key={c.id} onMouseDown={(e) => { e.preventDefault(); addToken(c.name); }} onMouseEnter={() => setHi(i)}
              style={{ padding: '9px 12px', cursor: 'pointer', background: i === hi ? '#eff6ff' : 'white', fontSize: 14, color: '#1f2937' }}>
              ✡️ {certOptionLabel(c)}
            </div>
          ))}
          {canAddFree && (
            <div onMouseDown={(e) => { e.preventDefault(); addToken(input); }} onMouseEnter={() => setHi(matches.length)}
              style={{ padding: '9px 12px', cursor: 'pointer', background: hi === matches.length ? '#eff6ff' : 'white', fontSize: 14, color: '#9a3412', borderTop: matches.length ? `1px solid ${C.border}` : 'none' }}>
              ✍️ {t('Add')} “{input.trim()}” {t('as a one-off hechsher')}
            </div>
          )}
        </div>
      )}
      {tokens.some((t) => !names.has(t.toLowerCase())) && (
        <div style={{ background: '#fff7ed', color: '#9a3412', padding: 8, borderRadius: 6, fontSize: 12, marginTop: 8 }}>
          ✍️ {t("A name that isn’t a listed certifier stores as plain text — no logo or “places we certify” link. Perfect for a one-off hechsher.")}
        </div>
      )}
    </div>
  );
}
